import { useAsync, useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, constants } from 'ethers'
import React, { useContext, useState } from 'react'
import { ArcContext } from 'src/contexts/ArcContext'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import { curvePoolAddresses, CurveService } from 'src/services/CurveService'
import useDebounce from 'src/utils/useDebounce'
import { ArcButton } from '../ArcButton'
import { CurveNotice } from '../CurveNotice'
import { Input } from '../Input'
import { Spinner } from '../Spinner'
import './Withdraw.scss'
import { WithdrawInfo } from './WithdrawInfo'

interface IProps {
  curveService: CurveService
}

export function Withdraw({ curveService }: IProps) {
  const { isLoading, setIsLoading } = useContext(ArcContext)

  const tokens = Object.keys(curvePoolAddresses)

  const [targetToken, setTargetToken] = useState<string>(tokens[0])
  const lpTokenBalance = useErc20Balance(curveService.lpToken.address, curveService.signer)
  const targetTokenDetails = useErc20Details(curvePoolAddresses[targetToken], curveService.signer)
  const [withdrawAmount, setWithdrawAmount] = useState(constants.Zero)
  const debouncedWithdrawAmount = useDebounce(withdrawAmount, 1000)

  const [expectedAmount] = useAsync<BigNumber>(
    () =>
      curveService.calcExpectedSingleWithdrawAmount(
        curvePoolAddresses[targetToken],
        debouncedWithdrawAmount,
      ),
    [debouncedWithdrawAmount, targetToken],
  )
  if (!curveService) {
    return <Spinner />
  }

  return (
    <div className="withdraw">
      <div className="withdraw__lp">
        <p className="withdraw__lp-description">LP Tokens Available</p>
        <p className="withdraw__lp-balance">{getReadableNumber(lpTokenBalance)}</p>
      </div>
      <Input
        decimals={18}
        label="Redeem for"
        assets={tokens.map((erc20Name) => ({
          label: erc20Name,
          value: erc20Name,
        }))}
        onAssetChanged={setTargetToken}
        setAmount={setWithdrawAmount}
        selectedAsset={targetToken}
        maxAmount={lpTokenBalance}
      />
      <WithdrawInfo
        expectedAmount={expectedAmount}
        targetToken={targetToken}
        targetTokenDecimals={targetTokenDetails?.decimals}
      />
      <ArcButton
        onClick={async () => {
          try {
            setIsLoading(true)
            await curveService.withdrawSingle(
              curvePoolAddresses[targetToken],
              debouncedWithdrawAmount,
              expectedAmount,
            )
          } finally {
            setIsLoading(false)
          }
        }}
        name="Withdraw"
        disabled={
          !expectedAmount ||
          !lpTokenBalance ||
          lpTokenBalance.lt(debouncedWithdrawAmount) ||
          lpTokenBalance.lt(withdrawAmount)
        }
        loading={isLoading}
      />
      <CurveNotice />
    </div>
  )
}
